import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { services } from "../utils/services";

const MachineLearningDetails = () => {
  const navigate = useNavigate();
  const service = services.find(s => s.slug === 'machine-learning');

  if (!service) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#030303] text-white">
        <h1 className="text-3xl font-bold mb-4 text-blue-400">
          Service Not Found
        </h1>
        <button
          onClick={() => navigate("/")}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-md transition-all duration-300"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#030303] to-[#101010] text-white px-6 md:px-20 py-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl font-extrabold text-blue-500 mb-4">
          {service.name}
        </h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          {service.summary}
        </p>
      </motion.div>

      {/* Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-5xl mx-auto bg-[#1a1a1a] p-10 rounded-3xl shadow-lg shadow-blue-900/30 relative overflow-hidden"
      >
        {/* Decorative Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-700/10 to-blue-500/5 blur-3xl"></div>

        {/* Main Content */}
        <div className="relative z-10">
          <h2 className="text-2xl font-semibold mb-4 text-yellow-400">
            What We Offer
          </h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            Our machine learning service combines creativity, strategy, and innovation to deliver exceptional results. We focus on meeting your specific business goals through customized solutions that stand out in the market.
          </p>

          {/* Components Section */}
          {service.components && service.components.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-blue-400 mb-4">
                Key Components
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {service.components.map((component, index) => (
                  <div key={index} className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-2">
                      {component.name}
                    </h4>
                    <p className="text-gray-300 text-sm mb-4">{component.description}</p>
                    <div className="text-xs text-gray-400">
                      <strong>Technologies:</strong> {component.technologies.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Images Section */}
          {service.images && service.images.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-blue-400 mb-4">
                Gallery
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {service.images.map((image, index) => (
                  <img
                    key={index}
                    src={typeof image === 'string' ? image : image.url}
                    alt={typeof image === 'string' ? `${service.name} ${index + 1}` : image.description}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {service.videos && service.videos.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-blue-400 mb-4">
                Videos
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {service.videos.map((video, index) => (
                  <iframe
                    key={index}
                    src={(typeof video === 'string' ? video : video.url).replace('watch?v=', 'embed/')}
                    title={typeof video === 'string' ? `${service.name} Video ${index + 1}` : video.description}
                    className="w-full h-48 rounded-lg"
                    allowFullScreen
                  ></iframe>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <div className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <h3 className="text-xl font-semibold text-blue-400 mb-2">
                Key Benefits
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Strategic Planning and Execution</li>
                <li>Customized Design and Development</li>
                <li>Scalable and Secure Solutions</li>
                <li>Dedicated Ongoing Support</li>
              </ul>
            </div>

            <div className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <h3 className="text-xl font-semibold text-blue-400 mb-2">
                Why Choose Us?
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Experienced professionals in each domain</li>
                <li>Data-driven insights for smart decisions</li>
                <li>Commitment to client satisfaction</li>
                <li>Modern technologies & agile workflow</li>
              </ul>
            </div>
          </div>

          {/* CTA Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/contact")}
            className="mt-10 px-8 py-3 bg-blue-600 hover:bg-blue-500 rounded-md font-semibold text-white transition-all duration-300"
          >
            Get Started with {service.name}
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};

export default MachineLearningDetails;
